import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * GDPR/ePrivacy-aligned cookie consent.
 *
 * Principles enforced here:
 *  - Before consent, only strictly-necessary technologies run. Optional
 *    categories default to OFF and are never pre-selected.
 *  - "Accept all" and "Reject non-essential" are presented with equal clarity.
 *  - Consent is versioned + timestamped and can be withdrawn at any time via the
 *    permanent footer "Cookie preferences" link (dispatches `odin:open-cookie-preferences`).
 *  - The site works fully when optional cookies are rejected.
 *
 * Today ODIN loads NO analytics or marketing scripts, so this governs FUTURE
 * additions: gate any such script behind `useConsent().consent?.categories.analytics`
 * (or `.marketing`). Strictly-necessary auth/security cookies are set server-side
 * and are out of scope for opt-in (documented in docs/privacy/COOKIE_INVENTORY.md).
 */

export const CONSENT_VERSION = 1;
const STORAGE_KEY = "odin_cookie_consent";
const OPEN_EVENT = "odin:open-cookie-preferences";

export interface ConsentCategories {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}
export interface ConsentRecord {
  version: number;
  timestamp: string;
  categories: ConsentCategories;
}

interface ConsentContextValue {
  consent: ConsentRecord | null;
  openPreferences: () => void;
  save: (categories: Pick<ConsentCategories, "analytics" | "marketing">) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within <CookieConsentProvider>");
  return ctx;
}

function read(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    // Re-ask on version bumps (material changes) or malformed data.
    if (!parsed || parsed.version !== CONSENT_VERSION || !parsed.categories) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Fire from anywhere to reopen the preferences dialog (used by the footer link). */
export function openCookiePreferences(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function CookieConsentProvider({ children }: { children: ReactNode }): JSX.Element {
  const [consent, setConsent] = useState<ConsentRecord | null>(() => read());
  const [showBanner, setShowBanner] = useState<boolean>(() => read() === null);
  const [showModal, setShowModal] = useState(false);

  const persist = useCallback((categories: Pick<ConsentCategories, "analytics" | "marketing">) => {
    const record: ConsentRecord = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      categories: { necessary: true, analytics: categories.analytics, marketing: categories.marketing },
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      /* storage may be unavailable; consent simply won't persist */
    }
    setConsent(record);
    setShowBanner(false);
    setShowModal(false);
  }, []);

  const openPreferences = useCallback(() => setShowModal(true), []);

  useEffect(() => {
    const onOpen = (): void => setShowModal(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({ consent, openPreferences, save: persist }),
    [consent, openPreferences, persist],
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {showBanner ? (
        <CookieBanner
          onAcceptAll={() => persist({ analytics: true, marketing: true })}
          onRejectNonEssential={() => persist({ analytics: false, marketing: false })}
          onManage={() => setShowModal(true)}
        />
      ) : null}
      {showModal ? (
        <CookiePreferencesModal
          initial={consent?.categories ?? { necessary: true, analytics: false, marketing: false }}
          onClose={() => setShowModal(false)}
          onSave={persist}
        />
      ) : null}
    </ConsentContext.Provider>
  );
}

function CookieBanner({
  onAcceptAll,
  onRejectNonEssential,
  onManage,
}: {
  onAcceptAll: () => void;
  onRejectNonEssential: () => void;
  onManage: () => void;
}): JSX.Element {
  return (
    <div className="mkt-cookie" role="dialog" aria-label="Cookie consent" aria-live="polite">
      <div className="mkt-cookie__inner">
        <p className="mkt-cookie__text">
          ODIN uses only strictly-necessary cookies to run the site and keep you signed in. With your
          consent we may later use optional analytics to improve ODIN. You can accept, reject, or choose
          per category — and change your mind anytime. See our{" "}
          <Link to="/legal/cookies">Cookie Policy</Link>.
        </p>
        <div className="mkt-cookie__actions">
          <button type="button" className="mkt-btn mkt-btn--secondary" onClick={onRejectNonEssential}>
            Reject non-essential
          </button>
          <button type="button" className="mkt-btn mkt-btn--secondary" onClick={onManage}>
            Manage preferences
          </button>
          <button type="button" className="mkt-btn mkt-btn--primary" onClick={onAcceptAll}>
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}

function CookiePreferencesModal({
  initial,
  onClose,
  onSave,
}: {
  initial: ConsentCategories;
  onClose: () => void;
  onSave: (c: Pick<ConsentCategories, "analytics" | "marketing">) => void;
}): JSX.Element {
  const [analytics, setAnalytics] = useState(initial.analytics);
  const [marketing, setMarketing] = useState(initial.marketing);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="mkt-cookie-overlay" onClick={onClose}>
      <div
        className="mkt-cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mkt-cookie-modal__head">
          <h2 id="cookie-modal-title">Cookie preferences</h2>
          <button ref={closeRef} type="button" className="mkt-cookie-modal__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <p className="mkt-cookie-modal__lead">
          Choose which optional cookies ODIN may use. Strictly-necessary cookies are always on because the
          site cannot function without them. ODIN works fully with everything else switched off.
        </p>

        <div className="mkt-cookie-cat">
          <div className="mkt-cookie-cat__row">
            <strong>Strictly necessary</strong>
            <span className="mkt-cookie-cat__always">Always on</span>
          </div>
          <p>Sign-in, session security, CSRF protection and consent storage. Cannot be switched off.</p>
        </div>

        <label className="mkt-cookie-cat">
          <div className="mkt-cookie-cat__row">
            <strong>Analytics</strong>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              aria-label="Allow analytics cookies"
            />
          </div>
          <p>Privacy-respecting usage measurement to help us improve ODIN. Off by default.</p>
        </label>

        <label className="mkt-cookie-cat">
          <div className="mkt-cookie-cat__row">
            <strong>Marketing</strong>
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              aria-label="Allow marketing cookies"
            />
          </div>
          <p>Not currently used. Reserved for any future marketing measurement, only with your consent.</p>
        </label>

        <div className="mkt-cookie-modal__actions">
          <button type="button" className="mkt-btn mkt-btn--secondary" onClick={() => onSave({ analytics: false, marketing: false })}>
            Reject non-essential
          </button>
          <button type="button" className="mkt-btn mkt-btn--primary" onClick={() => onSave({ analytics, marketing })}>
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}
